import { useEffect, useRef } from "react";

/**
 * useCameraCompositor — runtime rotation / mirror / color-filter compositor
 * for an outbound MediaStream track.
 *
 * When `active` is true and a source video track exists, we:
 *   1. Pipe the track into a hidden <video>
 *   2. Draw each frame onto a <canvas> with rotation / horizontal mirror /
 *      brightness / contrast / saturation CSS-style filters applied
 *   3. Emit a new MediaStreamTrack via `canvas.captureStream()`
 *   4. Call `onCompositedTrack(track)` so the caller can `replaceTrack` on
 *      every PeerConnection + update the local preview
 *
 * Settings object shape:
 * {
 *   rotationDeg: 0..360    (video rotated clockwise by this many degrees)
 *   mirrorH:    boolean    (horizontal flip like a selfie mirror)
 *   brightness: 0..2       (1 = neutral)
 *   contrast:   0..2       (1 = neutral)
 *   saturation: 0..2       (1 = neutral)
 * }
 *
 * When `active` flips to false, we call `onCompositedTrack(null)` so the
 * caller can restore the original camera track.
 */
export function useCameraCompositor({
  active,
  settings,
  getSourceStream,
  onCompositedTrack,
}) {
  const settingsRef = useRef(settings);
  useEffect(() => { settingsRef.current = settings; }, [settings]);
  // Ref-stabilise callback identities so parent re-renders with fresh
  // arrow functions don't tear down the compositor (previously caused
  // a visible preview flash + dropped frames on viewers).
  const getSourceStreamRef = useRef(getSourceStream);
  useEffect(() => { getSourceStreamRef.current = getSourceStream; }, [getSourceStream]);
  const onCompositedTrackRef = useRef(onCompositedTrack);
  useEffect(() => { onCompositedTrackRef.current = onCompositedTrack; }, [onCompositedTrack]);

  const rafRef = useRef(null);
  const producedStreamRef = useRef(null);

  useEffect(() => {
    if (!active) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      try { producedStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
      producedStreamRef.current = null;
      try { onCompositedTrackRef.current?.(null); } catch {}
      return;
    }

    const src = getSourceStreamRef.current?.();
    const videoTrack = src?.getVideoTracks?.()[0];
    if (!videoTrack) {
      try { onCompositedTrackRef.current?.(null); } catch {}
      return;
    }

    const settings0 = videoTrack.getSettings?.() || {};
    const baseW = settings0.width || 1280;
    const baseH = settings0.height || 720;

    const v = document.createElement("video");
    v.srcObject = new MediaStream([videoTrack]);
    v.muted = true;
    v.playsInline = true;
    v.autoplay = true;
    v.play().catch(() => {});

    const c = document.createElement("canvas");
    // Pick canvas aspect based on rotation — 90/270° swap w/h so the output
    // frame matches what the host sees on preview.
    const isSwapped = () => {
      const r = Math.round((settingsRef.current?.rotationDeg || 0) % 360);
      return r === 90 || r === 270;
    };
    c.width  = isSwapped() ? baseH : baseW;
    c.height = isSwapped() ? baseW : baseH;

    const ctx = c.getContext("2d");

    const tick = () => {
      const s = settingsRef.current || {};
      const rot = ((s.rotationDeg || 0) % 360 + 360) % 360;
      const swapped = rot === 90 || rot === 270;
      const targetW = swapped ? baseH : baseW;
      const targetH = swapped ? baseW : baseH;
      if (c.width !== targetW || c.height !== targetH) {
        c.width = targetW;
        c.height = targetH;
      }

      if (v.readyState >= 2) {
        ctx.save();
        ctx.clearRect(0, 0, c.width, c.height);
        // CSS-style color filters
        const b = s.brightness ?? 1;
        const k = s.contrast ?? 1;
        const sat = s.saturation ?? 1;
        if (b !== 1 || k !== 1 || sat !== 1) {
          ctx.filter = `brightness(${b}) contrast(${k}) saturate(${sat})`;
        }
        // Move origin to center and rotate
        ctx.translate(c.width / 2, c.height / 2);
        ctx.rotate((rot * Math.PI) / 180);
        // Horizontal mirror
        if (s.mirrorH) ctx.scale(-1, 1);
        // Draw the video centered around new origin at its natural size
        ctx.drawImage(v, -baseW / 2, -baseH / 2, baseW, baseH);
        ctx.restore();
        ctx.filter = "none";
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    tick();

    const producedStream = c.captureStream(30);
    producedStreamRef.current = producedStream;
    const producedTrack = producedStream.getVideoTracks()[0];
    try { onCompositedTrackRef.current?.(producedTrack); } catch {}

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      try { producedStream.getTracks().forEach((t) => t.stop()); } catch {}
      producedStreamRef.current = null;
      try { v.srcObject = null; } catch {}
    };
    // eslint-disable-next-line
    // Effect deps collapsed to `[active]` only — getSourceStream +
    // onCompositedTrack live in refs above so the compositor isn't
    // needlessly recycled on every parent re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
}

/**
 * Build a CSS `filter` string for the local <video> preview that reflects
 * the same transforms the outbound compositor will apply — so the host
 * sees exactly what the viewer sees.
 */
export function cameraSettingsToCss(s) {
  if (!s) return {};
  const rot = ((s.rotationDeg || 0) % 360 + 360) % 360;
  const mirror = s.mirrorH ? " scaleX(-1)" : "";
  const transform = rot || mirror ? `rotate(${rot}deg)${mirror}` : undefined;
  const b = s.brightness ?? 1;
  const k = s.contrast ?? 1;
  const sat = s.saturation ?? 1;
  const filter = b !== 1 || k !== 1 || sat !== 1
    ? `brightness(${b}) contrast(${k}) saturate(${sat})`
    : undefined;
  const style = {};
  if (transform) style.transform = transform;
  if (filter) style.filter = filter;
  return style;
}

/**
 * Detect whether the current video track supports the flashlight / torch
 * constraint on a mobile device. Not all browsers support it; Chrome on
 * Android does, iOS Safari does not.
 */
export function cameraSupportsTorch(stream) {
  const track = stream?.getVideoTracks?.()[0];
  if (!track) return false;
  try {
    const caps = track.getCapabilities?.() || {};
    return !!caps.torch;
  } catch { return false; }
}

/**
 * Turn the torch / flashlight on or off on the active video track, if the
 * track supports it. No-op otherwise.
 */
export async function setCameraTorch(stream, on) {
  const track = stream?.getVideoTracks?.()[0];
  if (!track) return false;
  try {
    const caps = track.getCapabilities?.() || {};
    if (!caps.torch) return false;
    await track.applyConstraints({ advanced: [{ torch: !!on }] });
    return true;
  } catch { return false; }
}
